<template>
  <span>
    <router-link active-class="active-class" v-if="route" id="btn" :to="`${route}`">
      <button class="custom-btn" type="button" v-bind="buttonAttrs" @click="onClick">
        <MyIcon class="button-icon" v-if="iconName" :name="iconName" :is-svg="true" size="16" />
        <span v-if="!hideLabel" class="button-label">
          <slot>{{ label }}</slot>
        </span>
        <slot v-else />
        <MyIcon
          class="button-icon-right"
          v-if="iconRight"
          :name="`${iconRight}`"
          :is-svg="true"
          size="16"
        />
      </button>
    </router-link>
    <a v-else-if="link" id="btn" :href="`${link}`" target="blank">
      <button class="custom-btn" type="button" v-bind="buttonAttrs" @click="onClick">
        <MyIcon class="button-icon" v-if="iconName" :name="iconName" :is-svg="true" size="16" />
        <span v-if="!hideLabel" class="button-label">
          <slot>{{ label }}</slot>
        </span>
        <slot v-else />
        <MyIcon
          class="button-icon-right"
          v-if="iconRight"
          :name="`${iconRight}`"
          :is-svg="true"
          size="16"
        />
      </button>
    </a>
    <button v-else class="custom-btn" type="button" v-bind="buttonAttrs" @click="onClick">
      <MyIcon class="button-icon" v-if="iconName" :name="iconName" :is-svg="true" size="16" />
      <span v-if="!hideLabel" class="button-label">
        <slot>{{ label }}</slot>
      </span>
      <slot v-else />
      <MyIcon
        class="button-icon-right"
        v-if="iconRight"
        :name="`${iconRight}`"
        :is-svg="true"
        size="16"
      />
    </button>
  </span>
</template>

<script>
import { reactive, computed } from 'vue';
import MyIcon from '../Icon.vue';

export default {
  name: 'my-button',
  components: {
    MyIcon,
  },
  props: {
    label: {
      type: String,
      required: false,
      default: 'Button Label',
    },
    ariaLabel: {
      type: String,
      default: '',
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    route: {
      type: String,
      required: false,
    },
    link: {
      type: String,
      required: false,
    },
    type: {
      type: String,
      validator: function (value) {
        return ['solid', 'outline', 'ghost', 'subtle', 'textlink', 'pill'].indexOf(value) !== -1;
      },
    },
    size: {
      type: String,
      validator: function (value) {
        return ['xs', 'small', 'large'].indexOf(value) !== -1;
      },
    },
    /* An icon path under assets/images (e.g. 'icon/print.svg'), drawn before
       the label. 'left' and 'right' are the older star markers, drawn in CSS. */
    icon: {
      type: String,
      required: false,
    },
    iconRight: {
      type: String,
      required: false,
    },
    customBgColor: {
      type: String,
      required: false,
    },
    customTextColor: {
      type: String,
      required: false,
    },
    /* State for toggles and menu triggers. Left unset, the attribute isn't
       rendered, so a plain button doesn't announce itself as a toggle. */
    ariaPressed: {
      type: [Boolean, String],
      default: null,
    },
    ariaExpanded: {
      type: [Boolean, String],
      default: null,
    },
    ariaHaspopup: {
      type: String,
      default: null,
    },
  },

  emits: ['click'],

  setup(props, { emit }) {
    props = reactive(props);
    const STAR_MARKERS = ['none', 'left', 'right'];
    const ariaState = (value) => (value === null || value === undefined ? undefined : String(value));
    const classes = computed(() => ({
      button: true,
      [`button--${props.type || 'solid'}`]: true,
      [`button--${props.size || 'large'}`]: true,
      [`button--${STAR_MARKERS.includes(props.icon) ? props.icon : 'none'}`]: true,
      external: !!props.link,
    }));
    const buttonStyles = computed(() => {
      const styles = {};
      if (props.customBgColor) {
        styles.backgroundColor = props.customBgColor;
      }
      if (props.customTextColor) {
        styles.color = props.customTextColor;
      }
      return styles;
    });
    return {
      classes,
      buttonStyles,
      iconName: computed(() =>
        props.icon && !STAR_MARKERS.includes(props.icon) ? props.icon : null
      ),
      buttonAttrs: computed(() => ({
        class: classes.value,
        style: buttonStyles.value,
        disabled: props.disabled,
        'aria-disabled': props.disabled ? 'true' : 'false',
        'aria-label': props.ariaLabel || props.label,
        'aria-pressed': ariaState(props.ariaPressed),
        'aria-expanded': ariaState(props.ariaExpanded),
        'aria-haspopup': props.ariaHaspopup || undefined,
      })),
      onClick() {
        if (props.disabled) return;
        emit('click');
      },
    };
  },
};
</script>
<style lang="scss" scoped>
* {
  font-family: inherit;
}
.button-icon {
  margin-inline-end: var(--spacing-xxs); // Adjust spacing between icon and label
  line-height: var(--lineHeight-none);
  // Add any other styling as needed for the icon
}

.button-icon-right {
  margin-inline-start: var(--spacing-xxs); // Adjust spacing between label and icon
  line-height: var(--lineHeight-none);
}

/* The link wrapping a button is still an <a>, so the global link underline
   would reach the label. A button never carries one. */
#btn,
#btn:hover {
  text-decoration: none !important;
}

/* .button-label intentionally left unstyled */
.button {
  border-radius: var(--spacing-xxs);
  /* The stroke is part of the height contract, so the base states it and the
     variants below only recolour it. One width across solid/outline/ghost/
     subtle — they used to split 2px/1px, which left the lighter variants 2px
     shorter than both the solid button and a text field beside them. */
  border: var(--control-border-width) solid transparent;
  cursor: pointer;
  display: inline-block;
  font-size: inherit;
  --reading-fontWeight: inherit;
  font-weight: var(--fontWeight-bold);
  /* Stated rather than left at `normal`: a text input of the same size reads
     the same token, so the two land on one height instead of depending on
     whatever leading the font's metrics happen to imply. */
  line-height: var(--control-lineHeight);
  // padding: 0.5rem 1rem 0.5rem 1rem;
  text-decoration: none;
  transition: all var(--duration-fast) var(--ease-standard);
  // &:hover {
  //   animation: animate-shake 1s;
  //   animation-delay: 1s;
  // }
  &:active {
    /* animation: animate-glow 1s;  */
    transform: scale(0.98);
    /* box-shadow: var(--shadow-text); */
  }
  &:disabled,
  &[aria-disabled='true'] {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.button--solid {
  color: var(--foreground-reversed) !important;
  background: var(--background-reversed) !important;
  border: var(--control-border-width) solid var(--background-reversed) !important;
  &:before,
  &:after {
    color: var(--foreground-reversed) !important;
  }
  &:hover {
    opacity: 0.85;
  }
}

/* On a reversed band the solid button's fill (--background-reversed) is the same
   colour as the band itself, so it vanishes. Swap the pair: the ink colour
   becomes the fill and vice versa. Holds in both schemes, since both tokens
   flip together. */
.reversed .button--solid {
  color: var(--background-reversed) !important;
  background: var(--foreground-reversed) !important;
  border: var(--control-border-width) solid var(--foreground-reversed) !important;
  &:before,
  &:after {
    color: var(--background-reversed) !important;
  }
}

.button--outline {
  background-color: transparent !important;
  color: var(--foreground) !important;
  border: var(--control-border-width) solid var(--foreground) !important;
  font-weight: var(--fontWeight-bold);

  &:before,
  &:after {
    color: var(--foreground) !important;
  }
  &:hover {
    color: var(--background) !important;
    background-color: var(--foreground) !important;
    &:before,
    &:after {
      color: var(--background) !important;
    }
  }
}

.button--ghost {
  color: var(--foreground) !important;
  background-color: transparent;
  border: var(--control-border-width) solid transparent !important;
  font-weight: var(--fontWeight-bold);
  &:before,
  &:after {
    color: var(--foreground) !important;
  }
  &:hover {
    background-color: var(--background-darker) !important;
  }
  // &:active,
  // .router-link-exact-active {
  //   border: dashed !important;
  // }
}
.reversed .button--ghost {
  color: var(--foreground-reversed) !important;
  &:hover {
    border: var(--control-border-width) solid var(--foreground-reversed) !important;
  }
  &:before,
  &:after {
    color: var(--foreground-reversed) !important;
  }
  &:active {
    // border: dashed !important;
    opacity: 0.5;
  }
}

.button--subtle {
  color: var(--foreground);
  background-color: transparent;
  border: var(--control-border-width) solid transparent !important;
  font-weight: var(--fontWeight-bold);
  &:before,
  &:after {
    color: inherit;
  }
  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
}

/* A text button: the same look as a text link (the global `a` rule in
   typography.scss and TextLink), for actions that don't go anywhere. The
   doubled class beats the size classes, which set the line height and weight
   of a filled button. */
.button.button--textlink {
  padding: 0 !important;
  color: var(--foreground) !important;
  font-weight: var(--fontWeight-medium);
  line-height: inherit;
  letter-spacing: inherit; /* a <button> resets it; a link inherits the body's */
  background-color: transparent;
  border: none;
  text-decoration: underline;
  text-underline-offset: var(--link-underline-offset);
  text-decoration-thickness: var(--link-underline-thickness) !important;
  &:before,
  &:after {
    color: inherit !important;
  }
  &:hover {
    color: var(--foreground);
    text-decoration: underline wavy var(--accent-line) !important;
    text-decoration-thickness: var(--link-underline-thickness-hover) !important;
  }
  &:active {
    transform: none;
    text-decoration: underline dashed !important;
    text-decoration-thickness: var(--link-underline-thickness) !important;
  }
}

/* PILL
   The article's Present, Print and Share, and the Library's List / Grid.
   Size sets the type (they use small); the padding is the pill's own. In a
   pair with aria-pressed, the pressed one keeps the outline and shadow and
   the other is a plain label that tints on hover. */
.button.button--pill {
  display: inline-flex;
  align-items: center;
  /* No spacing token lands on these; they're the pill's measured size. */
  padding: 0.85rem 1.15rem; /* design-guard:ignore */
  color: var(--foreground);
  background: var(--background);
  border: var(--border);
  border-radius: var(--radius-full);
  box-shadow: var(--shadow-light);
  line-height: var(--lineHeight-shortest);
  white-space: nowrap;
  user-select: none;

  &:hover {
    background: var(--background-darker);
  }
  &[aria-pressed='true'] {
    cursor: default;
  }
  &[aria-pressed='false'] {
    background: transparent;
    border-color: transparent;
    box-shadow: none;

    &:hover {
      background: var(--background-darker);
    }
  }
  .button-icon {
    display: inline-flex;
  }
}

.button--external.button--textlink .button-label::after {
  content: ' ↗';
  font-size: var(--font-glyph);
  margin-inline-start: 0.2em;
  display: inline-block;
}

.actve-class {
  text-decoration: underline dashed !important;
}

.button--xs {
  font-size: var(--font-400);
  --reading-fontWeight: var(--font-400-weight);
  font-weight: var(--font-400-weight);
  padding: var(--spacing-xxxs) var(--spacing-xxs);
}
.button--small {
  /* Body-scale token (16px desktop) so button text shrinks on mobile like body copy */
  font-size: var(--font-400);
  --reading-fontWeight: var(--font-400-weight);
  font-weight: var(--font-400-weight);
  /* Shared with .input-wrap--small input — see FORM CONTROLS in _config.scss */
  padding: var(--control-pad-block-start-sm) var(--control-pad-inline-sm)
    var(--control-pad-block-end-sm);

  /* border-radius: 100px; */
}
.button--large {
  /* Body-scale token (20px desktop) so button text shrinks on mobile like body copy */
  font-size: var(--font-500);
  --reading-fontWeight: var(--font-500-weight);
  font-weight: var(--font-500-weight);
  /* Shared with the default text input — see FORM CONTROLS in _config.scss */
  padding: var(--control-pad-block-start-lg) var(--control-pad-inline-lg)
    var(--control-pad-block-end-lg);
}

/* Icon Styles */
.button--none:before {
  content: '';
}
.button--left:before {
  content: '★ ';
  color: var(--foreground);
}
.button--right:after {
  content: ' ★';
  color: var(--foreground);
}

.reversed {
  &:before,
  &:after {
    color: var(--foreground-reversed);
  }
  .button--outline {
    color: var(--foreground-reversed) !important;
    border: var(--control-border-width) solid var(--foreground-reversed) !important;
    &:before,
    &:after {
      color: var(--foreground-reversed) !important;
    }
  }
  .button--outline:hover {
    color: var(--foreground) !important;
    background-color: var(--foreground-reversed) !important;
    &:before,
    &:after {
      color: var(--foreground) !important;
    }
  }
  .button--textlink {
    color: var(--foreground-reversed) !important;
    &:before,
    &:after {
      color: var(--foreground-reversed) !important;
    }
    &:hover {
      color: var(--foreground-reversed) !important;
    }
  }
  .button--external.button--textlink .button-label::after {
    color: var(--foreground-reversed) !important;
  }
}
</style>
